import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star } from "lucide-react";
import { resolveImg } from "@/lib/site";
import { useLanguage } from "@/lib/i18n";
import { CATEGORIES } from "@/lib/menuData";

const EASE = [0.22, 1, 0.36, 1];

export default function MenuSection() {
  const { t, lang, content } = useLanguage();
  const [active, setActive] = useState("petiscos");
  const menu = content.menu;
  const featured = content.featured;

  return (
    <section
      id="cardapio"
      data-testid="menu-section"
      className="relative bg-[#F4EBD9] py-24 sm:py-32"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="mb-14 max-w-2xl"
        >
          <p className="font-jetmono text-xs uppercase tracking-[0.25em] text-[#0D5C63]" data-testid="menu-eyebrow">
            {t.menu.eyebrow}
          </p>
          <h2 className="mt-4 font-playfair text-3xl leading-tight tracking-tight text-[#1A252C] sm:text-4xl lg:text-5xl">
            {t.menu.titleA}{" "}
            <span className="italic text-[#E76F51]">{t.menu.titleB}</span>
          </h2>
          <p className="mt-4 text-base leading-relaxed text-[#1A252C]/60 sm:text-lg">
            {t.menu.subtitle}
          </p>
        </motion.div>

        <div className="grid gap-12 lg:grid-cols-5 lg:gap-16">
          <motion.aside
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, ease: EASE }}
            className="lg:col-span-2"
          >
            <div className="lg:sticky lg:top-28" data-testid="menu-featured-card">
              <div className="overflow-hidden rounded-[2rem] border border-[#3D2B1F]/10 shadow-2xl shadow-[#3D2B1F]/15">
                <div className="relative">
                  <img
                    src={resolveImg(content.images.menuFeatured)}
                    alt="Barca do Rei servida na barca de madeira à beira-mar"
                    loading="lazy"
                    className="aspect-square w-full object-cover"
                  />
                  <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full bg-[#E9C46A] px-3.5 py-1.5 font-jetmono text-[10px] uppercase tracking-[0.18em] text-[#3D2B1F]">
                    <Star size={12} fill="currentColor" />
                    {t.menu.featured.badge}
                  </span>
                </div>
                <div className="bg-[#082F38] p-6 sm:p-7">
                  <h3 className="font-cabinet text-xl font-bold tracking-tight text-[#FAF6EE] sm:text-2xl">
                    {featured.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#F4EBD9]/70">
                    {t.menu.featured.desc}
                  </p>
                  <div className="mt-5">
                    <span className="font-jetmono text-xl text-[#E9C46A]">{featured.price}</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.aside>

          <div className="lg:col-span-3">
            <div
              data-testid="menu-tabs"
              className="mb-10 flex flex-wrap gap-2"
              role="tablist"
              aria-label={t.menu.eyebrow}
            >
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  data-testid={`menu-tab-${cat.id}`}
                  role="tab"
                  aria-selected={active === cat.id}
                  onClick={() => setActive(cat.id)}
                  className={`relative rounded-full px-5 py-2.5 font-cabinet text-sm font-bold transition-colors duration-300 ${
                    active === cat.id
                      ? "text-[#FAF6EE]"
                      : "text-[#1A252C]/60 hover:text-[#E76F51]"
                  }`}
                >
                  {active === cat.id && (
                    <motion.span
                      layoutId="menu-tab-pill"
                      className="absolute inset-0 rounded-full bg-[#0D5C63]"
                      transition={{ type: "spring", stiffness: 380, damping: 32 }}
                    />
                  )}
                  <span className="relative z-10">{t.menu.categories[cat.id]}</span>
                </button>
              ))}
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={active + lang}
                data-testid={`menu-list-${active}`}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.45, ease: EASE }}
                className="space-y-12"
              >
                {menu[active].map((group) => (
                  <div key={group.group}>
                    <div className="mb-6 flex flex-wrap items-baseline gap-x-4 gap-y-1">
                      <h3 className="font-playfair text-2xl italic text-[#0D5C63]">
                        {t.menu.groups[group.group]}
                      </h3>
                      {group.note && (
                        <span className="font-jetmono text-[10px] uppercase tracking-[0.18em] text-[#1A252C]/45">
                          {t.menu.notes[group.note]}
                        </span>
                      )}
                    </div>
                    <ul className="space-y-7">
                      {group.items.map((item) => (
                        <li
                          key={item.name}
                          data-testid={`menu-item-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className="group"
                        >
                          <div className="flex items-start gap-2 sm:gap-3">
                            <h4 className="min-w-0 flex-[0_1_auto] font-cabinet text-lg font-bold leading-snug tracking-tight text-[#1A252C] transition-colors duration-300 group-hover:text-[#E76F51] sm:text-xl">
                              {item.name}
                            </h4>
                            <span aria-hidden="true" className="mt-4 min-w-2 flex-1 border-b border-dotted border-[#3D2B1F]/25" />
                            <span className="min-w-[4.25rem] shrink-0 whitespace-nowrap text-right font-jetmono text-base tabular-nums text-[#E76F51] sm:min-w-[4.75rem] sm:text-lg">
                              {item.price}
                            </span>
                          </div>
                          {item.desc && (
                            <p className="mt-1.5 max-w-xl text-sm leading-relaxed text-[#1A252C]/55">
                              {item.desc[lang]}
                            </p>
                          )}
                          {item.tags.length > 0 && (
                            <div className="mt-2.5 flex flex-wrap gap-2">
                              {item.tags.map((tag) => (
                                <span
                                  key={tag}
                                  className="rounded-full border border-[#0D5C63]/25 px-3 py-1 font-jetmono text-[10px] uppercase tracking-[0.15em] text-[#0D5C63]"
                                >
                                  {t.menu.tags[tag]}
                                </span>
                              ))}
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
